import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  DragEvent,
  ChangeEvent,
} from "react";

import { useHistory, useLocation, useRouteMatch } from "react-router-dom";

import Layout from "../../components/Layout";
import Loading from "../../components/Loading";

import { useAuth } from "../../hooks/auth";

import api from "../../services/api";
import {
  fetchKeyLocations,
  patchPropertyKeyLocation,
  resolveKeyLocationId,
  type KeyLocationCatalogItem,
} from "../../services/propertyKeyLocation";
import {
  buildKeyLocationPatchBody,
  isDigitalKeyLocation,
  isOthersKeyLocation,
  KEY_LOCATION_PASSWORD_REQUIRED_MESSAGE,
  validateKeyLocationNoteForOthers,
  validateKeyLocationPasswordForDigital,
} from "../../utils/keyLocationRules";
import { readPropertyKeyLocationPassword } from "../../utils/visitKeyLocation";

import getValidationErrors, { Errors } from "../../utils/getValidationError";
import {
  normalizePhotoOrder,
  reorderPhotosAfterDrop,
  sanitizePhotoReorderQueue,
  validatePhotoReorderQueue,
} from "../../utils/photoOrder";
import refreshToken from "../../utils/refreshToken";
import {
  isDraftStatus,
  PropertyStatus,
} from "../../global/propertyStatus";
import {
  fetchPropertyDraft,
  publishProperty,
  updatePropertyDraft,
} from "../../services/propertyDraft";
import { validateDraftMinimum } from "../../utils/propertyDraftValidation";
import { yupPropertyAreaString } from "../../utils/propertyArea";
import { parseFurnitureStatus } from "../../utils/propertyFurniture";
import {
  applyCommissionToPayload,
  commissionFormFromApi,
  emptyCommissionForm,
  firstCommissionError,
  validatePropertyCommission,
  type PropertyCommissionForm,
} from "../../utils/propertyCommission";
import { extractParentPropertyId } from "../../utils/resolvePropertyNavigationId";
import {
  parseDraftMoneyInput,
  sanitizeDraftUpdatePayload,
} from "../../utils/propertyDraftPayload";
import {
  applyPropertyOwnerToPayload,
  hasPropertyOwner,
  resolveOwnerStateAfterDraftSave,
  type PropertyOwnerUserData,
} from "../../utils/propertyOwnerPayload";
import { sanitizePropertyDescriptionField } from "../../utils/propertyPlainTextSanitizer";
import {
  applyPropertyAlternativeContactToPayload,
  normalizePropertyAlternativeContactFromApi,
  optionalAlternativeContactYupFields,
  validatePropertyAlternativeContact,
} from "../../utils/propertyAlternativeContact";
import {
  HOUSING_PROGRAM_NONE,
  normalizeHousingProgramForApi,
  normalizeHousingProgramFromApi,
} from "../../utils/housingProgram";
import {
  validateAddressStep,
  validateCharacteristicsStep,
  validateDescriptionStep,
  validatePhotosForPublish,
  validatePropertyDataStep,
  validatePublicationStep,
  stepForDescriptionErrors,
} from "../../utils/editPropertyStepValidation";
import DraftBadge from "../../components/DraftBadge";
import { useOwnerEmailSearch } from "../../hooks/useOwnerEmailSearch";

import { geocodeByPlaceId } from "react-google-places-autocomplete";

import { useDropzone } from "react-dropzone";

import * as Yup from "yup";

import GeoCode from "react-geocode";

import WizardSteps from "../../components/WizardSteps";

import loadFile from "../../utils/loadFile";
import {
  extractPropertyPhotoBase64,
  inspectPropertyPhotoFile,
  mapUploadedPropertyPhoto,
  getApiPhotoUploadReason,
  PHOTO_DROPZONE_OPTIONS,
  PHOTO_INVALID_TYPE_REASON,
  shouldIgnorePhotoDrop,
  tooManyPropertyPhotosError,
} from "../../utils/propertyPhotoUpload";
import { useToast } from "../../hooks/toast";
import { useRefresh } from "../../hooks/refresh";
import { usePhotoAiEdit } from "../../hooks/usePhotoAiEdit";
import PhotoAiPromptModal from "../../components/PhotoAiPromptModal";
import { useConsultant } from "../../hooks/consultant";
import asyncForEach from "../../utils/asyncForEach";
import { mergeConsultantLists } from "../../utils/mergeConsultantLists";

import {
  EDIT_STEPS,
  ZAP_PUBLICATION_OPTIONS,
  FLOOR_TYPE_OPTIONS,
  EditPropertyWizardProvider,
  EditMapStep,
  EditDescriptionStep,
  EditPublicationStep,
  EditCharacteristicsStep,
  EditPhotosStep,
  EditHistoryStep,
  type EditPropertyWizardModel,
  type EditPropertyStepId,
} from "./components";

interface IParams {
  id: string;
}

interface IPhotos {
  id: number;
  order: number;
  url_l: string;
  url_m: string;
  url_s: string;
}

interface IConsultants {
  id: number;
  first_name: string;
}

interface IFinders {
  id: number;
  first_name: string;
}

interface IImageError {
  name: string;
  reason: string;
}

interface IImagesWithError {
  name: string;
  reason: string;
}

interface IStateProps {
  isToShowPhotos: string;
  imagesWithError: IImagesWithError[];
}

interface IPhotosToReOrder {
  id: number;
  newOrder: number;
}

interface IRentReasons {
  id: number;
  reason: string;
}

interface IRent {
  consultant_id: number | null;
  final_rent_value: string | null;
  id: number;
  operation: number;
  property_id: number;
  reason_id: null | number;
  rent_date: string | null;
  rented_by: string;
  rented_by_new: string;
  tenant_id: number | null;
  tenant_email: string | undefined;
}

interface IGuarantee {
  id: number;
  name: string;
  documents: string[];
  description: string;
}

interface IOwnerDetails {
  name: string;
  phone: string;
}

const EditProperty: React.FC = () => {
  const history = useHistory();

  const location = useLocation<IStateProps>();
  const { state } = location;

  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();
  const { handleRefresh, refresh } = useRefresh();

  const { params } = useRouteMatch<IParams>();

  const [loadingApiData, setLoadingApiData] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingGoogleMapsApi, setLoadingGoogleMapsApi] = useState(true);
  const [loadingSharedLink, setLoadingSharedLink] = useState(false);

  const [loadingPhotosRequest, setLoadingPhotosRequest] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [formOptionSelected, setFormOptionSelected] = useState(
    !!state ? state.isToShowPhotos : "map"
  );

  const [googleMapsSearch, setGoogleMapsSearch] = useState("");

  const [photos, setPhotos] = useState<IPhotos[]>([]);

  const [rentData, setRentData] = useState({} as IRent);

  const [consultants, setConsultants] = useState<IConsultants[]>([]);

  const [finders, setFinders] = useState<IFinders[]>([]);
  const [finderID, setFinderID] = useState<number>();
  const [finderName, setFinderName] = useState<string | undefined>("");
  const [loadingFinders, setLoadingFinders] = useState(true);

  const [addressID, setAddressID] = useState<number>();
  const [logradouro, setLogradouro] = useState("");
  const [cep, setCEP] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [floor, setFloor] = useState("");
  const [city, setCity] = useState("");
  const [uf, setUF] = useState("");
  const [googleID, setGoogleID] = useState("");
  const [hasChildrenUnits, setHasChildrenUnits] = useState(false);

  const [isRented, setIsRented] = useState<number>();
  const [propertyType, setPropertyType] = useState<number>();
  const [zapPublicationType, setZapPublicationType] = useState<string>(
    ZAP_PUBLICATION_OPTIONS.STANDARD
  );
  const [floorType, setFloorType] = useState<string>("");
  const [housingProgram, setHousingProgram] = useState<string>(
    HOUSING_PROGRAM_NONE
  );
  const [ownerID, setOwnerID] = useState<number>();
  const [rank, setRank] = useState<number>();
  const [ownerEmail, setOwnerEmail] = useState<string | undefined>("");
  const [ownerUserData, setOwnerUserData] = useState<
    PropertyOwnerUserData | undefined
  >();
  const [consultantID, setConsultantID] = useState<number>();
  const [propertyConsultant, setPropertyConsultant] = useState<
    string | undefined
  >("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [briefDescription, setBriefDescription] = useState("");
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [note, setNote] = useState("");
  const [rooms, setRooms] = useState<number>();
  const [suites, setSuites] = useState<number>();
  const [bathrooms, setBathrooms] = useState<number>();
  const [garage, setGarage] = useState<number>();
  const [rentValue, setRentValue] = useState("");
  const [condominiumValue, setCondominiumValue] = useState("");
  const [iptuValue, setIptuValue] = useState("");
  const [garbageTax, setGarbageTax] = useState("");
  const [area, setArea] = useState("");
  const [allowPets, setAllowPets] = useState<number>();
  const [isFunished, setIsFunished] = useState<number>();
  const [waterIncluded, setWaterIncluded] = useState<number>();
  const [gasIncluded, setGasIncluded] = useState<number>();
  const [isAdm, setIsAdm] = useState<number>();
  const [commission, setCommission] = useState<PropertyCommissionForm>(
    emptyCommissionForm()
  );
  const [yearBuilt, setYearBuilt] = useState("");
  const [condominium, setCondominium] = useState("");
  const [virtualTourUrl, setVirtualTourUrl] = useState("");
  const [keyLocationCatalog, setKeyLocationCatalog] = useState<
    KeyLocationCatalogItem[]
  >([]);
  const [keyLocationId, setKeyLocationId] = useState<number | null>(null);
  const [keyLocationNote, setKeyLocationNote] = useState("");
  const [keyLocationPassword, setKeyLocationPassword] = useState("");
  const [initialKeyLocationId, setInitialKeyLocationId] = useState<
    number | null
  >(null);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [isNearbyMajorRoads, setIsNearbyMajorRoads] = useState<number>();
  const [isNearbyMall, setIsNearbyMall] = useState<number>();
  const [isNearbyPublicTransport, setIsNearbyPublicTransport] =
    useState<number>();
  const [isNearbySchools, setIsNearbySchools] = useState<number>();
  const [isNearbyClinics, setIsNearbyClinics] = useState<number>();

  const [has24hConcierge, setHas24hConcierge] = useState<number>();
  const [hasAC, setHasAC] = useState<number>();
  const [hasBackyard, setHasBackyard] = useState<number>();
  const [hasBBQ, setHasBBQ] = useState<number>();
  const [hasCableTV, setHasCableTV] = useState<number>();
  const [hasElevator, setHasElevator] = useState<number>();
  const [hasGameRoom, setHasGameRoom] = useState<number>();
  const [hasGarden, setHasGarden] = useState<number>();
  const [hasGourmet, setHasGourmet] = useState<number>();
  const [hasGourmetPorch, setHasGourmetPorch] = useState<number>();
  const [hasGym, setHasGym] = useState<number>();
  const [hasHomeCinema, setHasHomeCinema] = useState<number>();
  const [hasParty, setHasParty] = useState<number>();
  const [hasPlayground, setHasPlayground] = useState<number>();
  const [hasPool, setHasPool] = useState<number>();
  const [hasPorch, setHasPorch] = useState<number>();
  const [hasSauna, setHasSauna] = useState<number>();
  const [hasSeaView, setHasSeaView] = useState<number>();
  const [hasSPA, setHasSPA] = useState<number>();
  const [hasSports, setHasSports] = useState<number>();
  const [hasStorage, setHasStorage] = useState<number>();
  const [hasTennisCourt, setHasTennisCourt] = useState<number>();

  const [rentReasons, setRentReasons] = useState<IRentReasons[]>([]);

  const [dragId, setDragId] = useState<string>();

  const [photosToReOrder, setPhotosToReOrder] = useState<IPhotosToReOrder[]>(
    []
  );

  const [latitude, setLatitude] = useState<number>(-3.72422);
  const [longitude, setLongitude] = useState<number>(-38.50403);

  const [formErrors, setFormErrors] = useState({} as Errors);

  const [imageError, setImageError] = useState<IImageError[]>([]);

  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loadingRequestToRemovePhoto, setLoadingRequestToRemovePhoto] =
    useState(false);

  const [openModalDeactivateProperty, setOpenModalDeactivateProperty] =
    useState(false);
  const [
    openModalConfirmActivateProperty,
    setOpenModalConfirmActivateProperty,
  ] = useState(false);
  const [openModalEditDeactivateProperty, setOpenModalEditDeactivateProperty] =
    useState(false);

  const [
    loadingRequestToActivateProperty,
    setLoadingRequestToActivateProperty,
  ] = useState(false);

  const [email, setEmail] = useState("");
  const [guarantees, setGuarantees] = useState<IGuarantee[]>();
  const [selectedGuarantees, setSelectedGuarantees] = useState<number[]>([]);

  const [ownerDetails, setOwnerDetails] = useState<IOwnerDetails>({
    name: "",
    phone: "",
  });

  const {
    results: users,
    isFetching: loadingUsersRequest,
    isOpen: isOwnerDropdownOpen,
    closeDropdown: closeOwnerDropdown,
    unlockSearch: unlockOwnerSearch,
    onSearchInputChange,
  } = useOwnerEmailSearch({
    accessToken: access_token,
    dateToExpires,
    onEmptyResults: () => {
      addToast({
        title: "Nenhum usuário encontrado",
        type: "error",
      });
    },
  });

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEmail(onSearchInputChange(e));
    },
    [onSearchInputChange]
  );

  const handleSelectUser = useCallback(
    (user_id: number | undefined) => {
      const user_selected = users.find((client) => client.id === user_id);
      closeOwnerDropdown();

      if (!!user_selected) {
        setOwnerID(user_selected.id);
        setOwnerEmail(user_selected?.email);
        setEmail(user_selected?.email);
        setOwnerUserData(undefined);
        setOwnerDetails({
          name: user_selected?.first_name ?? "",
          phone: user_selected?.phone ?? "",
        });
      }
    },
    [closeOwnerDropdown, users]
  );

  const handleClearOwnerSearchResults = useCallback(() => {
    closeOwnerDropdown();
  }, [closeOwnerDropdown]);

  const handleClearOwnerSelection = useCallback(() => {
    setOwnerID(undefined);
    setOwnerEmail("");
    setEmail("");
    setOwnerUserData(undefined);
    setOwnerDetails({ name: "", phone: "" });
    closeOwnerDropdown();
    unlockOwnerSearch();
  }, [closeOwnerDropdown, unlockOwnerSearch]);

  const handleAddNewOwner = useCallback(
    (userData: PropertyOwnerUserData) => {
      closeOwnerDropdown();
      setOwnerUserData(userData);
      setOwnerID(undefined);
      setOwnerEmail(userData.email);
      setEmail(userData.email);
      setOwnerDetails({
        name: userData.first_name,
        phone: userData.phone,
      });
    },
    [closeOwnerDropdown]
  );

  const syncOwnerFromDraftSave = useCallback((savedUserId: number | undefined) => {
    if (savedUserId == null) return;

    const nextOwner = resolveOwnerStateAfterDraftSave(
      {
        user_id: ownerID,
        user_data: ownerUserData,
      },
      savedUserId
    );

    if (nextOwner.user_id != null) {
      setOwnerID(nextOwner.user_id);
    }
    setOwnerUserData(undefined);
  }, [ownerID, ownerUserData]);

  const handlePhotoApplied = useCallback((newPhoto: Record<string, unknown>) => {
    setPhotos((prev) => [
      ...prev,
      {
        id: newPhoto.id as number,
        order: prev.length + 1,
        url_l: (newPhoto.url_l as string) || (newPhoto.photo as string),
        url_m: (newPhoto.url_m as string) || (newPhoto.photo as string),
        url_s: (newPhoto.url_s as string) || (newPhoto.photo as string),
      },
    ]);
  }, []);

  const {
    aiPreviews,
    generatingAI,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    isPromptModalOpen,
    isPromptModalLoading,
    promptModalPhotoId,
    promptModalPhotoUrl,
    promptDraft,
    setPromptDraft,
    openCustomEditPrompt,
    closeCustomEditPrompt,
    cancelGeneration,
    submitCustomEdit,
    handleGenerateVirtualStaging,
    handleApplyAI,
    handleDiscardAI,
    handleTryAgainAI,
  } = usePhotoAiEdit({
    accessToken: access_token,
    dateToExpires,
    photos,
    onPhotoApplied: handlePhotoApplied,
    onPreviewGenerated: () => setIsPreviewModalOpen(true),
  });

  const consultantsOptions = useMemo(() => {
    return consultants.map((consultant) => ({
      label: consultant.first_name,
      value: consultant.id,
    }));
  }, [consultants]);

  const findersOptions = useMemo(() => {
    if (!Array.isArray(finders)) return [];
    return finders.map((finder) => ({
      label: finder.first_name,
      value: finder.id,
    }));
  }, [finders]);

  const numberOfGaragesSuitesOptions = useMemo(() => {
    return [
      { label: "0", value: 0 },
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5+", value: 5 },
    ];
  }, []);

  const numberOfRoomsBathroomsOptions = useMemo(() => {
    return [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5", value: 5 },
      { label: "6", value: 6 },
      { label: "7", value: 7 },
      { label: "8", value: 8 },
      { label: "9", value: 9 },
      { label: "10", value: 10 },
    ];
  }, []);

  const numberOfRankOptions = useMemo(() => {
    return [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5", value: 5 },
    ];
  }, []);

  const formatPhotos = useMemo(() => normalizePhotoOrder(photos), [photos]);

  const loadFinders = useCallback(async () => {
    try {
      setLoadingFinders(true);
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

      const response = await api.get("/api/adm/users/finders");
      const findersData = response.data.data.finders;
      setFinders(findersData);

      if (finderID) {
        const finderFound = findersData.find((f: any) => f.id === finderID);
        if (finderFound) setFinderName(finderFound.first_name);
      }
    } catch (err: any) {
      console.log(err);
      setFinders([]);
    } finally {
      setLoadingFinders(false);
    }
  }, [access_token, dateToExpires, finderID]);

  useEffect(() => {
    async function loadApi() {
      try {
        setLoadingApiData(true);
        setLoadingGoogleMapsApi(true);

        const token = await refreshToken(dateToExpires, access_token);

        api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
          }`;

        const requestedId = Number(params.id);
        const response = await api.get(
          `/api/adm/properties/${requestedId}`
        );

        const parentId = extractParentPropertyId(response.data);
        if (parentId != null && parentId !== requestedId) {
          history.replace(`/property/${parentId}${location.search || ""}`);
          return;
        }

        setRentData(response.data.data.property.current_rent);

        setPhotos(response.data.data.property.photos);

        setAddressID(response.data.data.property.address.id);
        setLogradouro(response.data.data.property.address.address);
        setCEP(response.data.data.property.address.cep);
        setNumber(
          !!response.data.data.property.address.number
            ? response.data.data.property.address.number
            : ""
        );
        setComplement(
          !!response.data.data.property.address.complement
            ? response.data.data.property.address.complement
            : ""
        );
        setNeighborhood(response.data.data.property.address.neighborhood);
        setFloor(response.data.data.property.address.floor);
        setCity(response.data.data.property.address.city);
        setUF(response.data.data.property.address.uf);
        setLatitude(response.data.data.property.address.latitude);
        setLongitude(response.data.data.property.address.longitude);
        setGoogleID(response.data.data.property.address.google_id);

        setIsRented(response.data.data.property.is_rented);
        setPropertyType(response.data.data.property.property_type_id);
        const propertyStatus = response.data.data.property.status as
          | string
          | undefined;
        setIsDraft(
          Boolean(response.data.data.is_draft) || isDraftStatus(propertyStatus)
        );
        const loadedZapType = response.data.data.property.zap_publication_type;
        setZapPublicationType(
          loadedZapType || ZAP_PUBLICATION_OPTIONS.STANDARD
        );
        setFloorType(response.data.data.property.floor_type);
        setHousingProgram(
          normalizeHousingProgramFromApi(
            response.data.data.property.housing_program
          )
        );
        const rawProperty = response.data.data.property as {
          user_id?: number | null;
          owner?: {
            id?: number;
            user_id?: number | null;
            email?: string | null;
            first_name?: string | null;
            phone?: string | null;
            user?: { id?: number; email?: string | null };
          } | null;
          owner_email?: string | null;
          user?: { email?: string | null; id?: number };
        };
        const owner = rawProperty.owner;
        const resolvedOwnerEmail = [
          owner?.email,
          owner?.user?.email,
          rawProperty.owner_email,
          rawProperty.user?.email,
        ].find((v) => typeof v === "string" && v.trim().length > 0);

        const resolvedOwnerId = [
          rawProperty.user_id,
          rawProperty.user?.id,
          owner?.user_id,
          owner?.user?.id,
        ]
          .map((id) => (id != null ? Number(id) : NaN))
          .find((id) => Number.isFinite(id) && id > 0);

        setOwnerID(resolvedOwnerId);
        setRank(response.data.data.property.rank);
        setOwnerEmail(resolvedOwnerEmail ?? "");
        setOwnerUserData(undefined);
        setOwnerDetails({
          name: owner?.first_name ?? "",
          phone: owner?.phone ?? "",
        });
        setEmail(resolvedOwnerEmail ?? "");
        setConsultantID(response.data.data.property.consultant_id);
        setFinderID(response.data.data.property.finder_id);

        setTitle(response.data.data.property.title ?? "");
        setDescription(response.data.data.property.description ?? "");
        setRooms(response.data.data.property.rooms);
        setSuites(response.data.data.property.suites);
        setBathrooms(response.data.data.property.bathrooms);
        setGarage(response.data.data.property.garages);
        setRentValue(
          response.data.data.property.rent_value == null
            ? ""
            : String(response.data.data.property.rent_value).replaceAll(".", "")
        );
        setIptuValue(
          response.data.data.property.iptu_value == null
            ? ""
            : String(response.data.data.property.iptu_value).replaceAll(".", "")
        );
        setGarbageTax(
          response.data.data.property.garbage_tax == null
            ? ""
            : String(response.data.data.property.garbage_tax).replaceAll(".", "")
        );
        setCondominiumValue(
          response.data.data.property.condominium_value == null
            ? ""
            : String(response.data.data.property.condominium_value).replaceAll(
                ".",
                ""
              )
        );
        setCondominium(response.data.data.property.condominium || "");
        setVirtualTourUrl(response.data.data.property.virtual_tour_url || "");

        let catalog: KeyLocationCatalogItem[] = [];
        try {
          catalog = await fetchKeyLocations(api);
        } catch {
          catalog = [];
        }
        setKeyLocationCatalog(catalog);
        const resolvedKeyLocationId = resolveKeyLocationId(
          response.data.data.property,
          catalog
        );
        setKeyLocationId(resolvedKeyLocationId);
        setInitialKeyLocationId(resolvedKeyLocationId);
        setKeyLocationNote(
          response.data.data.property.key_location_note ?? ""
        );
        setKeyLocationPassword(
          readPropertyKeyLocationPassword(response.data.data.property)
        );
        const alternativeContact = normalizePropertyAlternativeContactFromApi(
          response.data.data.property
        );
        setContactEmail(alternativeContact.contact_email);
        setContactPhone(alternativeContact.contact_phone);

        setArea(
          response.data.data.property.area == null
            ? ""
            : String(response.data.data.property.area)
        );
        setIsFunished(parseFurnitureStatus(response.data.data.property.is_furnished));
        setAllowPets(response.data.data.property.allow_pets);
        setWaterIncluded(response.data.data.property.water_included);
        setGasIncluded(response.data.data.property.gas_included);
        setIsAdm(response.data.data.property.is_adm);
        setCommission(commissionFormFromApi(response.data.data.property));
        setYearBuilt(response.data.data.property.year_built);

        setIsNearbyMajorRoads(
          response.data.data.property.is_nearby_major_roads
        );
        setIsNearbyMall(response.data.data.property.is_nearby_mall);
        setIsNearbyPublicTransport(
          response.data.data.property.is_nearby_public_transport
        );
        setIsNearbySchools(response.data.data.property.is_nearby_schools);
        setIsNearbyClinics(response.data.data.property.is_nearby_clinics);

        setHas24hConcierge(response.data.data.property.has_24h_concierge);
        setHasAC(response.data.data.property.has_ac);
        setHasBackyard(response.data.data.property.has_backyard);
        setHasBBQ(response.data.data.property.has_bbq);
        setHasCableTV(response.data.data.property.has_cable_tv);
        setHasElevator(response.data.data.property.has_elevator);
        setHasGameRoom(response.data.data.property.has_game_room);
        setHasGarden(response.data.data.property.has_garden);
        setHasGourmet(response.data.data.property.has_gourmet);
        setHasGourmetPorch(response.data.data.property.has_gourmet_porch);
        setHasGym(response.data.data.property.has_gym);
        setHasHomeCinema(response.data.data.property.has_home_cinema);
        setHasParty(response.data.data.property.has_party);
        setHasPlayground(response.data.data.property.has_playground);
        setHasPool(response.data.data.property.has_pool);
        setHasPorch(response.data.data.property.has_porch);
        setHasSauna(response.data.data.property.has_sauna);
        setHasSeaView(response.data.data.property.has_sea_view);
        setHasSPA(response.data.data.property.has_spa);
        setHasSports(response.data.data.property.has_sports);
        setHasStorage(response.data.data.property.has_storage);
        setHasTennisCourt(response.data.data.property.has_tennis_court);

        setLoadingApiData(false);

        setRentReasons(response.data.data.rentReasons);

        setSelectedGuarantees(
          response.data.data.property.guarantees.map(
            (guarantee: IGuarantee) => {
              return guarantee.id;
            }
          )
        );
        setNote(response.data.data.property.note);
      } catch (err: any) {
        console.log(err);

        if (!!err.response?.data.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }

        setLoadingApiData(false);
      }
    }

    async function loadGuarantees() {
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
          }`;
        const response = await api.get("/api/adm/guarantees");
        setGuarantees(response.data.data);
      } catch (err: any) {
        console.log(err);
        if (!!err.response?.data.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      }
    }
    if (!!user) {
      loadApi();
      loadGuarantees();
    } else {
      history.push("/");
    }
  }, [
    dateToExpires,
    access_token,
    user,
    history,
    location.search,
    params.id,
    refresh,
    addToast,
  ]);

  const { handleAddConsultants, consultantsSaved } = useConsultant();

  useEffect(() => {
    async function loadApi() {
      try {
        const token = await refreshToken(dateToExpires, access_token);

        if (!loadingApiData) {
          api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
            }`;
          if (consultantsSaved.length < 1) {
            const [response, brokersRes] = await Promise.all([
              api.get("/api/adm/users", {
                params: {
                  role: "consultant",
                },
              }),
              api.get("/api/adm/users/partner-brokers").catch(() => ({
                data: {
                  data: { partner_brokers: { data: [] as IConsultants[] } },
                },
              })),
            ]);

            const merged = mergeConsultantLists(
              response.data.data.consultants,
              brokersRes.data?.data?.partner_brokers?.data
            );

            handleAddConsultants(merged);

            const consultant_name = merged.find(
              (consultant: { id: number }) => consultant.id === consultantID
            );

            setConsultants(merged);

            setPropertyConsultant(
              !!consultant_name ? consultant_name.first_name : ""
            );
          } else {
            const consultant_name = consultantsSaved.find(
              (consultant: { id: number }) => consultant.id === consultantID
            );

            setConsultants(consultantsSaved);

            setPropertyConsultant(
              !!consultant_name ? consultant_name.first_name : ""
            );

            return;
          }
        }
        setLoadingUsers(false);
      } catch (err: any) {
        console.log(err);

        if (!!err.response?.data.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }

        setLoadingApiData(false);
      }
    }
    if (!!user) {
      loadApi();
    } else {
      history.push("/");
    }
  }, [
    consultantsSaved,
    handleAddConsultants,
    dateToExpires,
    access_token,
    user,
    history,
    params.id,
    addToast,
    loadingApiData,
    consultantID
  ]);

  useEffect(() => {
    if (!!user && !loadingApiData) {
      loadFinders();
    }
  }, [user, loadingApiData, loadFinders]);

  const handleGoToDescriptionForm = useCallback(async () => {
    setLoadingRequest(true);

    try {
      const data = {
        logradouro: logradouro,
        cep: cep,
        number: number,
        complement: complement,
        neighborhood: neighborhood,
        floor: floor,
        city: city,
        uf: uf,
      };

      const cepRegex = /^\d{5}-\d{3}$/;

      const schema = Yup.object().shape({
        logradouro: Yup.string()
          .trim()
          .required("O campo Logradouro é obrigatório"),
        cep: Yup.string()
          .trim()
          .required("O campo CEP é obrigatório")
          .matches(cepRegex, "CEP inválido"),
        number: Yup.string()
          .trim()
          .required("O campo Número é obrigatório")
          .matches(/^\d+$/, "O campo Número deve conter apenas dígitos"),
        neighborhood: Yup.string()
          .trim()
          .required("O campo Bairro é obrigatório"),
        city: Yup.string().trim().required("O campo Cidade é obrigatório"),
        uf: Yup.string().trim().required("O campo UF é obrigatório"),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const response = await api.put(`/api/adm/addresses/${addressID}`, {
        cep: data.cep,
        address: data.logradouro,
        complement: data.complement,
        number: Number(data.number),
        city: data.city,
        uf: data.uf,
        neighborhood: data.neighborhood,
        floor: data.floor,
        latitude: String(latitude),
        longitude: String(longitude),
        google_id: googleID,
      });

      if (!!response.data.success) {
        setFormErrors({});

        addToast({
          title: "Endereço atualizado!",
          type: "success",
        });

        handleRefresh();
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);

        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });

        setFormErrors(error);
      } else if (
        !!err.response?.data.errors &&
        !!err.response.data.errors[Object.keys(err.response.data.errors)[0]][0]
      ) {
        const errorsNumber = Object.keys(err.response.data.errors).length;

        for (let i = 0; i < errorsNumber; i++) {
          err.response.data.errors[
            Object.keys(err.response.data.errors)[i]
          ].forEach((error: string) => {
            addToast({
              title: error,
              type: "error",
            });
          });
        }
      }

      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }
    }

    setLoadingRequest(false);
  }, [
    logradouro,
    cep,
    number,
    complement,
    neighborhood,
    floor,
    city,
    uf,
    latitude,
    longitude,
    googleID,
    addressID,
    access_token,
    dateToExpires,
    addToast,
    handleRefresh,
  ]);

  const handlePublishOnImovelWeb = useCallback(async () => {
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const response = await api.post(
        `/api/adm/publish-on-imovel-web/${params.id}`
      );

      if (response.data.success) {
        addToast({
          title: "Imóvel publicado no imovelWeb",
          type: "success",
        });

        handleRefresh();
      }
    } catch (err: any) {
      addToast({
        title: "Erro ao publicar no imovelWeb",
        type: "info",
        description: "",
      });
    }
  }, [handleRefresh, addToast, params.id, dateToExpires, access_token]);

  const buildDescriptionApiPayload = useCallback(() => {
    const toMoney = (raw: string) =>
      Number(
        `${raw.substring(0, raw.length - 2)}.${raw.substring(
          raw.length - 2,
          raw.length
        )}`
      );

    return applyCommissionToPayload(
      applyPropertyAlternativeContactToPayload(
      applyPropertyOwnerToPayload(
        {
          property_type_id: propertyType,
          zap_publication_type: zapPublicationType,
          floor_type: floorType,
          housing_program: normalizeHousingProgramForApi(housingProgram),
          consultant_id: consultantID,
          finder_id: finderID,
          address_id: addressID,
          title: title,
          description: sanitizePropertyDescriptionField(description) ?? "",
          rooms: rooms,
          suites: suites,
          bathrooms: bathrooms,
          garages: garage,
          rent_value: toMoney(rentValue),
          condominium_value: toMoney(condominiumValue),
          iptu_value: toMoney(iptuValue),
          garbage_tax: toMoney(garbageTax),
          area: Number(area.replaceAll("m²", "").replaceAll(",", ".")),
          is_furnished: isFunished,
          allow_pets: allowPets,
          rank: rank,
          water_included: waterIncluded,
          gas_included: gasIncluded,
          is_adm: isAdm,
          year_built: yearBuilt,
          guarantees: selectedGuarantees,
          note: note,
          condominium: condominium,
          virtual_tour_url:
            virtualTourUrl.trim() !== "" ? virtualTourUrl.trim() : null,
          key_location_id: keyLocationId,
          key_location_note:
            keyLocationNote.trim() !== "" ? keyLocationNote.trim() : null,
        },
        { user_id: ownerID, user_data: ownerUserData }
      ),
      {
        contact_email: contactEmail,
        contact_phone: contactPhone,
      }
      ),
      commission
    );
  }, [
    addressID,
    commission,
    allowPets,
    area,
    bathrooms,
    condominium,
    condominiumValue,
    consultantID,
    description,
    finderID,
    floorType,
    garage,
    garbageTax,
    gasIncluded,
    housingProgram,
    iptuValue,
    isAdm,
    isFunished,
    keyLocationId,
    keyLocationNote,
    note,
    ownerID,
    ownerUserData,
    contactEmail,
    contactPhone,
    propertyType,
    rank,
    rentValue,
    rooms,
    selectedGuarantees,
    suites,
    title,
    virtualTourUrl,
    waterIncluded,
    yearBuilt,
    zapPublicationType,
  ]);

  /**
   * Pré-cadastro PUT accepts partial bodies; omit empty/NaN fields that cause 422
   * when the draft is still incomplete (unlike the published-property PUT).
   */
  const buildDraftApiPayload = useCallback(() => {
    const areaNumber = Number(
      String(area ?? "")
        .replaceAll("m²", "")
        .replaceAll(",", ".")
    );

    // Prefer the users.id from the property relation. Sending a non-user owner.id
    // causes Laravel exists:users 422 on draft PUT.
    const payload = sanitizeDraftUpdatePayload({
      property_type_id: propertyType,
      zap_publication_type: zapPublicationType || undefined,
      floor_type: floorType || undefined,
      housing_program: normalizeHousingProgramForApi(housingProgram),
      consultant_id: consultantID,
      finder_id: finderID,
      address_id: addressID,
      title: title,
      description: sanitizePropertyDescriptionField(description),
      rooms: rooms,
      suites: suites,
      bathrooms: bathrooms,
      garages: garage,
      rent_value: parseDraftMoneyInput(rentValue),
      condominium_value: parseDraftMoneyInput(condominiumValue),
      iptu_value: parseDraftMoneyInput(iptuValue),
      garbage_tax: parseDraftMoneyInput(garbageTax),
      area: Number.isFinite(areaNumber) ? areaNumber : undefined,
      is_furnished: isFunished,
      allow_pets: allowPets,
      rank: rank,
      water_included: waterIncluded,
      gas_included: gasIncluded,
      is_adm: isAdm,
      year_built: yearBuilt || undefined,
      guarantees: selectedGuarantees,
      note: note,
      condominium: condominium,
      virtual_tour_url: virtualTourUrl,
      key_location_id:
        keyLocationId != null && Number.isFinite(Number(keyLocationId))
          ? Number(keyLocationId)
          : undefined,
      key_location_note:
        keyLocationNote.trim() !== "" ? keyLocationNote.trim() : undefined,
    });

    const withOwner = applyPropertyOwnerToPayload(payload, {
      user_id: ownerID,
      user_data: ownerUserData,
    });
    delete withOwner.partner_broker_id;

    return applyCommissionToPayload(
      applyPropertyAlternativeContactToPayload(withOwner, {
        contact_email: contactEmail,
        contact_phone: contactPhone,
      }),
      commission
    );
  }, [
    addressID,
    commission,
    allowPets,
    area,
    bathrooms,
    condominium,
    condominiumValue,
    consultantID,
    description,
    finderID,
    floorType,
    garage,
    garbageTax,
    gasIncluded,
    housingProgram,
    iptuValue,
    isAdm,
    isFunished,
    keyLocationId,
    keyLocationNote,
    note,
    ownerID,
    ownerUserData,
    contactEmail,
    contactPhone,
    propertyType,
    rank,
    rentValue,
    rooms,
    selectedGuarantees,
    suites,
    title,
    virtualTourUrl,
    waterIncluded,
    yearBuilt,
    zapPublicationType,
  ]);

  const toastDraftApiErrors = useCallback(
    (err: any) => {
      const apiErrors = err?.response?.data?.errors;
      if (
        apiErrors != null &&
        typeof apiErrors === "object" &&
        !Array.isArray(apiErrors)
      ) {
        Object.keys(apiErrors).forEach((key) => {
          const messages = apiErrors[key];
          if (Array.isArray(messages)) {
            messages.forEach((msg: string) => {
              addToast({ title: msg, type: "error" });
            });
          }
        });
        return true;
      }
      return false;
    },
    [addToast]
  );

  const persistKeyLocation = useCallback(async (): Promise<
    | { ok: true }
    | {
        ok: false;
        field: "key_location_note" | "key_location_password";
        message: string;
      }
  > => {
    const resolvedKeyLocationId =
      keyLocationId != null && Number.isFinite(Number(keyLocationId))
        ? Number(keyLocationId)
        : null;
    if (resolvedKeyLocationId == null) return { ok: true };

    const othersNoteErr = validateKeyLocationNoteForOthers(
      keyLocationCatalog,
      resolvedKeyLocationId,
      keyLocationNote
    );
    if (othersNoteErr) {
      return { ok: false, field: "key_location_note", message: othersNoteErr };
    }

    const passwordErr = validateKeyLocationPasswordForDigital(
      keyLocationCatalog,
      resolvedKeyLocationId,
      keyLocationPassword,
      {
        allowBlankToKeep: isDigitalKeyLocation(
          keyLocationCatalog,
          initialKeyLocationId
        ),
      }
    );
    if (passwordErr) {
      return {
        ok: false,
        field: "key_location_password",
        message: passwordErr,
      };
    }

    await patchPropertyKeyLocation(
      api,
      Number(params.id),
      buildKeyLocationPatchBody(keyLocationCatalog, {
        keyLocationId: resolvedKeyLocationId,
        keyLocationNote,
        keyLocationPassword,
      })
    );
    setInitialKeyLocationId(resolvedKeyLocationId);
    return { ok: true };
  }, [
    initialKeyLocationId,
    keyLocationCatalog,
    keyLocationId,
    keyLocationNote,
    keyLocationPassword,
    params.id,
  ]);

  const handleSaveDraft = useCallback(async () => {
    if (isSavingDraft) return;

    const contactErrors = validatePropertyAlternativeContact({
      contact_email: contactEmail,
      contact_phone: contactPhone,
    });
    if (Object.keys(contactErrors).length > 0) {
      setFormErrors(contactErrors);
      addToast({
        title: Object.values(contactErrors)[0],
        type: "error",
      });
      return;
    }

    const commissionErrors = validatePropertyCommission(commission);
    if (Object.keys(commissionErrors).length > 0) {
      setFormErrors(commissionErrors);
      addToast({
        title: firstCommissionError(commissionErrors),
        type: "error",
      });
      return;
    }

    setIsSavingDraft(true);
    try {
      const payload = buildDraftApiPayload();
      const minimumErrors = validateDraftMinimum(payload);
      if (minimumErrors.length > 0) {
        addToast({ title: minimumErrors[0], type: "error" });
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const keyLocationResult = await persistKeyLocation();
      if (!keyLocationResult.ok) {
        addToast({ title: keyLocationResult.message, type: "error" });
        setFormErrors({ [keyLocationResult.field]: keyLocationResult.message });
        return;
      }

      const savedDraft = await updatePropertyDraft(api, Number(params.id), payload);
      syncOwnerFromDraftSave(savedDraft.user_id);

      addToast({
        title: "Rascunho salvo com sucesso!",
        type: "success",
      });
      setFormErrors({});
    } catch (err: any) {
      if (toastDraftApiErrors(err)) return;
      if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      } else {
        addToast({
          title: "Erro ao salvar rascunho",
          type: "error",
        });
      }
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    access_token,
    addToast,
    buildDraftApiPayload,
    dateToExpires,
    isSavingDraft,
    persistKeyLocation,
    contactEmail,
    contactPhone,
    commission,
    params.id,
    syncOwnerFromDraftSave,
    toastDraftApiErrors,
  ]);

  const handleGoToCharacteristicForm = useCallback(async () => {
    setLoadingRequest(true);

    try {
      const data = {
        property_type_id: propertyType,
        zap_publication_type: zapPublicationType,
        floor_type: floorType,
        owner_email: (ownerUserData?.email || ownerEmail || email || "").trim(),
        property_consultant: propertyConsultant,
        title: title,
        description: sanitizePropertyDescriptionField(description) ?? "",
        rooms: rooms,
        suites: suites,
        bathrooms: bathrooms,
        garage: garage,
        rent_value: rentValue.replaceAll("R$", ""),
        condominium_value: condominiumValue.replaceAll("R$", ""),
        iptu_value: iptuValue.replaceAll("R$", ""),
        garbage_tax: garbageTax.replaceAll("R$", ""),
        area: area.replaceAll("m²", ""),
        is_funished: isFunished,
        allow_pets: allowPets,
        rank: rank,
        water_included: waterIncluded,
        gas_included: gasIncluded,
        is_adm: isAdm,
        guarantees: selectedGuarantees,
        note: note,
        condominium: condominium,
        virtual_tour_url: virtualTourUrl,
        key_location_id: keyLocationId,
        key_location_note: keyLocationNote,
        key_location_password: keyLocationPassword,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        commission_type: commission.commission_type,
        admin_percentage: commission.admin_percentage,
        first_rent_commission_7cantos_percentage:
          commission.first_rent_commission_7cantos_percentage,
        partner_name: commission.partner_name,
        partner_first_rent_commission_percentage:
          commission.partner_first_rent_commission_percentage,
      };

      const commissionErrors = validatePropertyCommission(commission);
      if (Object.keys(commissionErrors).length > 0) {
        addToast({
          title: firstCommissionError(commissionErrors),
          type: "error",
        });
        setFormErrors(commissionErrors);
        setLoadingRequest(false);
        return;
      }

      const othersNoteErr = validateKeyLocationNoteForOthers(
        keyLocationCatalog,
        keyLocationId,
        keyLocationNote
      );
      if (othersNoteErr) {
        addToast({ title: othersNoteErr, type: "error" });
        setFormErrors({ key_location_note: othersNoteErr });
        setLoadingRequest(false);
        return;
      }
      const passwordErr = validateKeyLocationPasswordForDigital(
        keyLocationCatalog,
        keyLocationId,
        keyLocationPassword,
        {
          allowBlankToKeep: isDigitalKeyLocation(
            keyLocationCatalog,
            initialKeyLocationId
          ),
        }
      );
      if (passwordErr) {
        addToast({ title: passwordErr, type: "error" });
        setFormErrors({ key_location_password: passwordErr });
        setLoadingRequest(false);
        return;
      }

      const schema = Yup.object().shape({
        property_type_id: Yup.number().required("Informe o tipo do imóvel"),
        zap_publication_type: Yup.string().required(
          "Informe o tipo do publicação no ZAP"
        ),
        owner_email: Yup.string().required(
          "E-mail do proprietário obrigatório"
        ),
        property_consultant: Yup.string().required("Consultante obrigatório"),
        title: Yup.string().required("Título obrigatório"),
        description: Yup.string().required("Descrição obrigatória"),
        rent_value: Yup.string().required("Valor do aluguel obrigatório"),
        condominium_value: Yup.string().required(
          "Valor do condomínio obrigatório"
        ),
        iptu_value: Yup.string().required("Valor do iptu obrigatório"),
        area: yupPropertyAreaString(),
        guarantees: Yup.array()
          .required("Mínimo de 1 garantia")
          .min(1, "Necessária ao menos 1 garantia"),
        condominium: Yup.string(),
        virtual_tour_url: Yup.string(),
        key_location_id: Yup.number()
          .nullable()
          .required("Localização da chave é obrigatória"),
        key_location_note: Yup.string()
          .max(255)
          .nullable()
          .when("key_location_id", {
            is: (id: number | null) =>
              isOthersKeyLocation(keyLocationCatalog, id ?? null),
            then: (schema) =>
              schema
                .trim()
                .required(
                  'Informe a observação da chave quando a opção é "Outros".'
                ),
            otherwise: (schema) => schema.nullable(),
          }),
        key_location_password: Yup.string()
          .nullable()
          .when("key_location_id", {
            is: (id: number | null) =>
              isDigitalKeyLocation(keyLocationCatalog, id ?? null) &&
              !isDigitalKeyLocation(keyLocationCatalog, initialKeyLocationId),
            then: (schema) =>
              schema.trim().required(KEY_LOCATION_PASSWORD_REQUIRED_MESSAGE),
            otherwise: (schema) => schema.nullable(),
          }),
        ...optionalAlternativeContactYupFields(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });
      setFormErrors((current) => {
        const next = { ...current };
        delete next.contact_email;
        delete next.contact_phone;
        return next;
      });

      if (
        !hasPropertyOwner({ user_id: ownerID, user_data: ownerUserData })
      ) {
        addToast({
          title: "Selecione ou cadastre um proprietário.",
          type: "error",
        });
        setFormErrors({
          owner_email: "Selecione ou cadastre um proprietário.",
        });
        setLoadingRequest(false);
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const keyLocationResult = await persistKeyLocation();
      if (!keyLocationResult.ok) {
        addToast({ title: keyLocationResult.message, type: "error" });
        setFormErrors({ [keyLocationResult.field]: keyLocationResult.message });
        setLoadingRequest(false);
        return;
      }

      const descriptionPayload = buildDescriptionApiPayload();

      const response = await api.put(
        `/api/adm/properties/${params.id}`,
        descriptionPayload
      );

      if (!!response.data.success) {
        setFormErrors({});

        addToast({
          title: "Descrição atualizada!",
          type: "success",
        });

        handleRefresh();
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);

        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });

        setFormErrors(error);
      } else if (
        !!err.response?.data.errors &&
        !!err.response.data.errors[Object.keys(err.response.data.errors)[0]][0]
      ) {
        const errorsNumber = Object.keys(err.response.data.errors).length;

        for (let i = 0; i < errorsNumber; i++) {
          err.response.data.errors[
            Object.keys(err.response.data.errors)[i]
          ].forEach((error: string) => {
            addToast({
              title: error,
              type: "error",
            });
          });
        }
      }

      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setLoadingRequest(false);
  }, [
    handleRefresh,
    addToast,
    params.id,
    buildDescriptionApiPayload,
    email,
    ownerEmail,
    ownerID,
    ownerUserData,
    propertyConsultant,
    propertyType,
    zapPublicationType,
    floorType,
    title,
    description,
    rooms,
    suites,
    bathrooms,
    garage,
    rentValue,
    condominiumValue,
    iptuValue,
    garbageTax,
    area,
    isFunished,
    allowPets,
    rank,
    dateToExpires,
    access_token,
    waterIncluded,
    gasIncluded,
    isAdm,
    selectedGuarantees,
    note,
    condominium,
    virtualTourUrl,
    keyLocationId,
    keyLocationNote,
    keyLocationPassword,
    keyLocationCatalog,
    initialKeyLocationId,
    persistKeyLocation,
    contactEmail,
    contactPhone,
    commission,
  ]);

  const buildAddressStepData = useCallback(
    () => ({
      logradouro,
      cep,
      number,
      complement,
      neighborhood,
      floor,
      city,
      uf,
    }),
    [cep, city, complement, floor, logradouro, neighborhood, number, uf]
  );

  const buildDescriptionStepData = useCallback(
    () => ({
      property_type_id: propertyType,
      zap_publication_type: zapPublicationType,
      owner_email: (ownerUserData?.email || ownerEmail || email || "").trim(),
      property_consultant: propertyConsultant,
      title,
      description,
      rooms,
      suites,
      bathrooms,
      garage,
      rent_value: rentValue.replaceAll("R$", ""),
      condominium_value: condominiumValue.replaceAll("R$", ""),
      iptu_value: iptuValue.replaceAll("R$", ""),
      area: area.replaceAll("m²", ""),
      is_funished: isFunished,
      allow_pets: allowPets,
      water_included: waterIncluded,
      gas_included: gasIncluded,
      guarantees: selectedGuarantees,
      condominium,
      virtual_tour_url: virtualTourUrl,
      key_location_id: keyLocationId,
      key_location_note: keyLocationNote,
      key_location_password: keyLocationPassword,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      commission_type: commission.commission_type,
      admin_percentage: commission.admin_percentage,
      first_rent_commission_7cantos_percentage:
        commission.first_rent_commission_7cantos_percentage,
      partner_name: commission.partner_name,
      partner_first_rent_commission_percentage:
        commission.partner_first_rent_commission_percentage,
    }),
    [
      allowPets,
      area,
      bathrooms,
      condominium,
      condominiumValue,
      contactEmail,
      contactPhone,
      description,
      email,
      garage,
      gasIncluded,
      iptuValue,
      isFunished,
      keyLocationId,
      keyLocationNote,
      keyLocationPassword,
      ownerEmail,
      ownerUserData,
      propertyConsultant,
      propertyType,
      rentValue,
      rooms,
      selectedGuarantees,
      suites,
      title,
      virtualTourUrl,
      waterIncluded,
      zapPublicationType,
      commission,
    ]
  );

  const buildCharacteristicsStepData = useCallback(
    () => ({
      rooms,
      suites,
      bathrooms,
      garage,
      is_funished: isFunished,
      allow_pets: allowPets,
      water_included: waterIncluded,
      gas_included: gasIncluded,
      condominium,
      is_nearby_clinics: isNearbyClinics,
      is_nearby_major_roads: isNearbyMajorRoads,
      is_nearby_schools: isNearbySchools,
      is_nearby_mall: isNearbyMall,
      is_nearby_public_transport: isNearbyPublicTransport,
      has_24h_concierge: has24hConcierge,
      has_ac: hasAC,
      has_backyard: hasBackyard,
      has_bbq: hasBBQ,
      has_cable_tv: hasCableTV,
      has_elevator: hasElevator,
      has_game_room: hasGameRoom,
      has_garden: hasGarden,
      has_gourmet: hasGourmet,
      has_gourmet_porch: hasGourmetPorch,
      has_gym: hasGym,
      has_home_cinema: hasHomeCinema,
      has_party: hasParty,
      has_playground: hasPlayground,
      has_pool: hasPool,
      has_porch: hasPorch,
      has_sauna: hasSauna,
      has_sea_view: hasSeaView,
      has_spa: hasSPA,
      has_sports: hasSports,
      has_storage: hasStorage,
      has_tennis_court: hasTennisCourt,
    }),
    [
      allowPets,
      bathrooms,
      condominium,
      garage,
      gasIncluded,
      has24hConcierge,
      hasAC,
      hasBBQ,
      hasBackyard,
      hasCableTV,
      hasElevator,
      hasGameRoom,
      hasGarden,
      hasGourmet,
      hasGourmetPorch,
      hasGym,
      hasHomeCinema,
      hasParty,
      hasPlayground,
      hasPool,
      hasPorch,
      hasSPA,
      hasSauna,
      hasSeaView,
      hasSports,
      hasStorage,
      hasTennisCourt,
      isFunished,
      isNearbyClinics,
      isNearbyMajorRoads,
      isNearbyMall,
      isNearbyPublicTransport,
      isNearbySchools,
      rooms,
      suites,
      waterIncluded,
    ]
  );

  const toastStepValidationFailure = useCallback(
    (firstMessage: string, errors: Errors, step?: EditPropertyStepId) => {
      addToast({
        title: firstMessage,
        type: "error",
        description: "",
      });
      setFormErrors(errors);
      if (step) {
        setFormOptionSelected(step);
        window.scrollTo(0, 0);
      }
    },
    [addToast]
  );

  const handleDraftNextFromMap = useCallback(() => {
    const result = validateAddressStep(buildAddressStepData());
    if (!result.ok) {
      toastStepValidationFailure(result.firstMessage, result.errors);
      return;
    }
    setFormErrors({});
    setFormOptionSelected("description");
    window.scrollTo(0, 0);
  }, [buildAddressStepData, toastStepValidationFailure]);

  const handleDraftNextFromDescription = useCallback(() => {
    const data = buildDescriptionStepData();
    const result = validatePropertyDataStep(data);
    if (!result.ok) {
      toastStepValidationFailure(result.firstMessage, result.errors);
      return;
    }
    setFormErrors({});
    setFormOptionSelected("publication");
    window.scrollTo(0, 0);
  }, [buildDescriptionStepData, toastStepValidationFailure]);

  const handleDraftNextFromPublication = useCallback(() => {
    const data = buildDescriptionStepData();
    const result = validatePublicationStep(data, keyLocationCatalog, {
      allowBlankDigitalPassword: isDigitalKeyLocation(
        keyLocationCatalog,
        initialKeyLocationId
      ),
    });
    if (!result.ok) {
      toastStepValidationFailure(result.firstMessage, result.errors);
      return;
    }
    setFormErrors({});
    setFormOptionSelected("characteristic");
    window.scrollTo(0, 0);
  }, [
    buildDescriptionStepData,
    initialKeyLocationId,
    keyLocationCatalog,
    toastStepValidationFailure,
  ]);

  const handleDraftNextFromCharacteristics = useCallback(() => {
    const result = validateCharacteristicsStep(buildCharacteristicsStepData());
    if (!result.ok) {
      toastStepValidationFailure(result.firstMessage, result.errors);
      return;
    }
    setFormErrors({});
    setFormOptionSelected("photos");
    window.scrollTo(0, 0);
  }, [buildCharacteristicsStepData, toastStepValidationFailure]);

  const handlePublishDraft = useCallback(async () => {
    if (loadingRequest) return;

    const addressResult = validateAddressStep(buildAddressStepData());
    if (!addressResult.ok) {
      toastStepValidationFailure(
        addressResult.firstMessage,
        addressResult.errors,
        "map"
      );
      return;
    }

    const descriptionResult = validateDescriptionStep(
      buildDescriptionStepData(),
      keyLocationCatalog,
      {
        allowBlankDigitalPassword: isDigitalKeyLocation(
          keyLocationCatalog,
          initialKeyLocationId
        ),
      }
    );
    if (!descriptionResult.ok) {
      toastStepValidationFailure(
        descriptionResult.firstMessage,
        descriptionResult.errors,
        stepForDescriptionErrors(descriptionResult.errors)
      );
      return;
    }

    if (!hasPropertyOwner({ user_id: ownerID, user_data: ownerUserData })) {
      toastStepValidationFailure(
        "Selecione ou cadastre um proprietário.",
        { owner_email: "Selecione ou cadastre um proprietário." },
        "publication"
      );
      return;
    }

    const characteristicsResult = validateCharacteristicsStep(
      buildCharacteristicsStepData()
    );
    if (!characteristicsResult.ok) {
      toastStepValidationFailure(
        characteristicsResult.firstMessage,
        characteristicsResult.errors,
        "characteristic"
      );
      return;
    }

    const photosResult = validatePhotosForPublish(photos.length);
    if (!photosResult.ok) {
      toastStepValidationFailure(photosResult.firstMessage, photosResult.errors);
      return;
    }


    setLoadingRequest(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const keyLocationResult = await persistKeyLocation();
      if (!keyLocationResult.ok) {
        addToast({ title: keyLocationResult.message, type: "error" });
        setFormErrors({ [keyLocationResult.field]: keyLocationResult.message });
        return;
      }

      const draftMeta = await fetchPropertyDraft(api, Number(params.id));
      if (draftMeta.publishBlockers.length > 0) {
        addToast({
          title: draftMeta.publishBlockers[0],
          type: "error",
        });
        return;
      }

      await publishProperty(
        api,
        Number(params.id),
        buildDescriptionApiPayload()
      );
      setIsDraft(false);
      setFormErrors({});
      addToast({
        title: "Imóvel publicado!",
        type: "success",
      });
      handleRefresh();
    } catch (err: any) {
      const apiErrors = err?.response?.data?.errors;
      if (
        apiErrors != null &&
        typeof apiErrors === "object" &&
        !Array.isArray(apiErrors)
      ) {
        Object.keys(apiErrors).forEach((key) => {
          const messages = apiErrors[key];
          if (Array.isArray(messages)) {
            messages.forEach((msg: string) => {
              addToast({ title: msg, type: "error" });
            });
          }
        });
      }
      if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      } else if (!err?.response?.data?.errors) {
        addToast({
          title: "Erro ao publicar imóvel",
          type: "error",
        });
      }
    } finally {
      setLoadingRequest(false);
    }
  }, [
    access_token,
    addToast,
    buildAddressStepData,
    buildCharacteristicsStepData,
    buildDescriptionApiPayload,
    buildDescriptionStepData,
    dateToExpires,
    handleRefresh,
    initialKeyLocationId,
    keyLocationCatalog,
    persistKeyLocation,
    loadingRequest,
    ownerID,
    ownerUserData,
    params.id,
    photos.length,
    toastStepValidationFailure,
  ]);

  const handleClearAddressFields = useCallback(() => {
    setLogradouro("");
    setCEP("");
    setNumber("");
    setComplement("");
    setNeighborhood("");
    setFloor("");
    setCity("");
    setUF("");
    setGoogleMapsSearch("");
  }, []);

  const handleSaveAddressDraft = useCallback(async () => {
    if (isSavingDraft) return;

    setIsSavingDraft(true);
    try {
      const data = {
        logradouro,
        cep,
        number,
        complement,
        neighborhood,
        floor,
        city,
        uf,
      };

      const cepRegex = /^\d{5}-\d{3}$/;
      const schema = Yup.object().shape({
        logradouro: Yup.string().required("Logradouro obrigatório"),
        cep: Yup.string()
          .required("CEP obrigatório")
          .matches(cepRegex, "CEP inválido"),
        number: Yup.string().required("Número obrigatório"),
        neighborhood: Yup.string().required("Bairro obrigatório"),
        city: Yup.string().required("Cidade obrigatória"),
        uf: Yup.string().required("UF obrigatório"),
      });

      await schema.validate(data, { abortEarly: false });

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      await api.put(`/api/adm/addresses/${addressID}`, {
        cep: data.cep,
        address: data.logradouro,
        complement: data.complement,
        number: Number(data.number),
        city: data.city,
        uf: data.uf,
        neighborhood: data.neighborhood,
        floor: data.floor,
        latitude: String(latitude),
        longitude: String(longitude),
        google_id: googleID,
      });

      const savedDraft = await updatePropertyDraft(
        api,
        Number(params.id),
        buildDraftApiPayload()
      );
      syncOwnerFromDraftSave(savedDraft.user_id);

      setFormErrors({});
      addToast({
        title: "Rascunho salvo com sucesso!",
        type: "success",
      });
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });
        setFormErrors(error);
      } else if (toastDraftApiErrors(err)) {
        return;
      } else if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      } else {
        addToast({
          title: "Erro ao salvar rascunho",
          type: "error",
        });
      }
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    access_token,
    addToast,
    addressID,
    buildDraftApiPayload,
    cep,
    city,
    complement,
    dateToExpires,
    floor,
    googleID,
    isSavingDraft,
    latitude,
    logradouro,
    longitude,
    neighborhood,
    number,
    params.id,
    syncOwnerFromDraftSave,
    toastDraftApiErrors,
    uf,
  ]);

  const handleSavePhotosDraft = useCallback(async () => {
    if (isSavingDraft) return;

    setIsSavingDraft(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const savedDraft = await updatePropertyDraft(
        api,
        Number(params.id),
        buildDraftApiPayload()
      );
      syncOwnerFromDraftSave(savedDraft.user_id);

      addToast({
        title: "Rascunho salvo com sucesso!",
        type: "success",
      });
    } catch (err: any) {
      if (toastDraftApiErrors(err)) return;
      addToast({
        title: err?.response?.data?.message ?? "Erro ao salvar rascunho",
        type: "error",
      });
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    access_token,
    addToast,
    buildDraftApiPayload,
    dateToExpires,
    isSavingDraft,
    params.id,
    syncOwnerFromDraftSave,
    toastDraftApiErrors,
  ]);

  const handleSaveCharacteristicsDraft = useCallback(async () => {
    if (isSavingDraft) return;

    setIsSavingDraft(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const characteristicsPayload = {
        is_nearby_clinics: isNearbyClinics,
        is_nearby_major_roads: isNearbyMajorRoads,
        is_nearby_schools: isNearbySchools,
        is_nearby_mall: isNearbyMall,
        is_nearby_public_transport: isNearbyPublicTransport,
        has_24h_concierge: has24hConcierge,
        has_ac: hasAC,
        has_backyard: hasBackyard,
        has_bbq: hasBBQ,
        has_cable_tv: hasCableTV,
        has_elevator: hasElevator,
        has_game_room: hasGameRoom,
        has_garden: hasGarden,
        has_gourmet: hasGourmet,
        has_gourmet_porch: hasGourmetPorch,
        has_gym: hasGym,
        has_home_cinema: hasHomeCinema,
        has_party: hasParty,
        has_playground: hasPlayground,
        has_pool: hasPool,
        has_porch: hasPorch,
        has_sauna: hasSauna,
        has_sea_view: hasSeaView,
        has_spa: hasSPA,
        has_sports: hasSports,
        has_storage: hasStorage,
        has_tennis_court: hasTennisCourt,
      };

      const savedDraft = await updatePropertyDraft(api, Number(params.id), {
        ...buildDraftApiPayload(),
        ...sanitizeDraftUpdatePayload(characteristicsPayload),
      });
      syncOwnerFromDraftSave(savedDraft.user_id);

      addToast({
        title: "Rascunho salvo com sucesso!",
        type: "success",
      });
    } catch (err: any) {
      if (toastDraftApiErrors(err)) return;
      if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      } else {
        addToast({
          title: "Erro ao salvar rascunho",
          type: "error",
        });
      }
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    access_token,
    addToast,
    buildDraftApiPayload,
    dateToExpires,
    has24hConcierge,
    hasAC,
    hasBBQ,
    hasBackyard,
    hasCableTV,
    hasElevator,
    hasGameRoom,
    hasGarden,
    hasGourmet,
    hasGourmetPorch,
    hasGym,
    hasHomeCinema,
    hasParty,
    hasPlayground,
    hasPool,
    hasPorch,
    hasSPA,
    hasSauna,
    hasSeaView,
    hasSports,
    hasStorage,
    hasTennisCourt,
    isNearbyClinics,
    isNearbyMajorRoads,
    isNearbyMall,
    isNearbyPublicTransport,
    isNearbySchools,
    isSavingDraft,
    params.id,
    syncOwnerFromDraftSave,
    toastDraftApiErrors,
  ]);

  const handleGoToPhotosForm = useCallback(async () => {
    setLoadingRequest(true);

    try {
      const characteristicsResult = validateCharacteristicsStep(
        buildCharacteristicsStepData()
      );
      if (!characteristicsResult.ok) {
        addToast({
          title: characteristicsResult.firstMessage,
          type: "info",
          description: "",
        });
        setFormErrors(characteristicsResult.errors);
        setLoadingRequest(false);
        return;
      }

      const data = {
        is_nearby_clinics: isNearbyClinics,
        is_nearby_major_roads: isNearbyMajorRoads,
        is_nearby_schools: isNearbySchools,
        is_nearby_mall: isNearbyMall,
        is_nearby_public_transport: isNearbyPublicTransport,
        has_24h_concierge: has24hConcierge,
        has_ac: hasAC,
        has_backyard: hasBackyard,
        has_bbq: hasBBQ,
        has_cable_tv: hasCableTV,
        has_elevator: hasElevator,
        has_game_room: hasGameRoom,
        has_garden: hasGarden,
        has_gourmet: hasGourmet,
        has_gourmet_porch: hasGourmetPorch,
        has_gym: hasGym,
        has_home_cinema: hasHomeCinema,
        has_party: hasParty,
        has_playground: hasPlayground,
        has_pool: hasPool,
        has_porch: hasPorch,
        has_sauna: hasSauna,
        has_sea_view: hasSeaView,
        has_spa: hasSPA,
        has_sports: hasSports,
        has_storage: hasStorage,
        has_tennis_court: hasTennisCourt,
      };

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      await api.put(
        `/api/adm/properties/${params.id}`,
        buildDescriptionApiPayload()
      );

      const response = await api.put(
        `/api/adm/properties/${params.id}/benefits`,
        {
          ...data,
        }
      );

      if (!!response.data.success) {
        setFormErrors({});

        addToast({
          title: "Características atualizada!",
          type: "success",
        });

        handleRefresh();
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);

        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });

        setFormErrors(error);
      } else if (
        !!err.response?.data.errors &&
        !!err.response.data.errors[Object.keys(err.response.data.errors)[0]][0]
      ) {
        const errorsNumber = Object.keys(err.response.data.errors).length;

        for (let i = 0; i < errorsNumber; i++) {
          err.response.data.errors[
            Object.keys(err.response.data.errors)[i]
          ].forEach((error: string) => {
            addToast({
              title: error,
              type: "error",
            });
          });
        }
      }

      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setLoadingRequest(false);
  }, [
    addToast,
    buildCharacteristicsStepData,
    buildDescriptionApiPayload,
    handleRefresh,
    dateToExpires,
    params.id,
    access_token,
    isNearbyClinics,
    isNearbyMajorRoads,
    isNearbySchools,
    isNearbyMall,
    isNearbyPublicTransport,
    has24hConcierge,
    hasAC,
    hasBackyard,
    hasBBQ,
    hasCableTV,
    hasElevator,
    hasGameRoom,
    hasGarden,
    hasGourmet,
    hasGourmetPorch,
    hasGym,
    hasHomeCinema,
    hasParty,
    hasPlayground,
    hasPool,
    hasPorch,
    hasSauna,
    hasSeaView,
    hasSPA,
    hasSports,
    hasStorage,
    hasTennisCourt,
  ]);

  const handleChangeOrders = useCallback(async () => {
    setLoadingRequest(true);
    try {

      if (photosToReOrder.length < 1) {
        setLoadingRequest(false);

        addToast({
          title: "Você não está reordenando nenhuma imagem",
          type: "error",
        });

        return;
      }

      const existingPhotoIds = photos.map((photo) => photo.id);
      const sanitizedQueue = sanitizePhotoReorderQueue(
        photosToReOrder,
        existingPhotoIds
      );

      if (sanitizedQueue.length < 1) {
        setPhotosToReOrder([]);
        setLoadingRequest(false);
        addToast({
          title:
            "Não há alterações válidas para a ordem das imagens. Reordena as fotos e tenta novamente.",
          type: "error",
        });
        return;
      }

      const validation = validatePhotoReorderQueue(
        sanitizedQueue,
        photos.length
      );

      if (!validation.ok) {
        setPhotosToReOrder([]);
        setLoadingRequest(false);
        const titles: Record<typeof validation.error, string> = {
          EMPTY_QUEUE:
            "Não há alterações válidas para a ordem das imagens. Reordena as fotos e tenta novamente.",
          INVALID_NEW_ORDER_RANGE:
            "A ordem das imagens ficou inconsistente. Reordena as fotos e tenta novamente.",
          DUPLICATE_ID:
            "A ordem das imagens ficou inconsistente. Reordena as fotos e tenta novamente.",
          DUPLICATE_NEW_ORDER:
            "A ordem das imagens ficou inconsistente. Reordena as fotos e tenta novamente.",
        };
        addToast({
          title: titles[validation.error],
          type: "error",
        });
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const response = await api.put(`/api/adm/photos`, {
        photos: sanitizedQueue,
      });

      if (!!response.data.success) {
        setFormErrors({});

        setPhotosToReOrder([]);

        addToast({
          title: "Imagens reordenadas!",
          type: "success",
        });

        handleRefresh();
      }
    } catch (err: any) {
      let toastShown = false;

      if (!!err.response?.data.errors) {
        const errorsNumber = Object.keys(err.response.data.errors).length;

        for (let i = 0; i < errorsNumber; i++) {
          err.response.data.errors[
            Object.keys(err.response.data.errors)[i]
          ].forEach((error: string) => {
            addToast({
              title: error,
              type: "error",
            });
          });
        }
        toastShown = true;
      }

      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
        toastShown = true;
      }

      if (!toastShown) {
        addToast({
          title:
            "Não foi possível guardar a ordem das imagens. Verifica a ligação e tenta novamente.",
          type: "error",
        });
      }
    }

    setLoadingRequest(false);
  }, [
    dateToExpires,
    access_token,
    photosToReOrder,
    photos,
    handleRefresh,
    addToast,
  ]);

  const hadleGetMoreInformationsAboutLocal = useCallback(
    async (address: string, place_id: string) => {
      try {
        GeoCode.setApiKey(
          !!process.env.REACT_APP_GOOGLE_MAPS_KEY
            ? process.env.REACT_APP_GOOGLE_MAPS_KEY
            : ""
        );

        const response = await GeoCode.fromAddress(address);

        const request = await geocodeByPlaceId(place_id);

        request[0].address_components.forEach((address) => {
          if (address.types.join().includes("route")) {
            setLogradouro(address.long_name);

            return;
          }

          if (address.types.join().includes("administrative_area_level_2")) {
            setCity(address.long_name);

            return;
          }

          if (address.types.join().includes("sublocality_level_1")) {
            setNeighborhood(address.long_name);

            return;
          }

          if (address.types.join().includes("administrative_area_level_1")) {
            setUF(address.short_name);

            return;
          }

          if (address.types.join().includes("street_number")) {
            setNumber(address.short_name);

            return;
          }

          if (address.types.join().includes("postal_code")) {
            setCEP(address.short_name);

            return;
          }
        });

        setLatitude(response.results[0].geometry.location.lat);
        setLongitude(response.results[0].geometry.location.lng);
      } catch (err: any) {
        console.log(err);
      }
    },
    []
  );

  const hadleDragImageBox = useCallback((ev: DragEvent<HTMLDivElement>) => {
    setDragId(ev.currentTarget.id);
  }, []);

  const handleDropImageBox = useCallback(
    (ev: DragEvent<HTMLDivElement>) => {
      if (!dragId) return;

      const { photos: reorderedPhotos, queue } = reorderPhotosAfterDrop(
        formatPhotos,
        dragId,
        ev.currentTarget?.id ?? ""
      );

      if (queue.length === 0) return;

      setPhotosToReOrder(queue);
      setPhotos(reorderedPhotos);
    },
    [formatPhotos, dragId]
  );

  const handleOnDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (shouldIgnorePhotoDrop(acceptedFiles)) {
        return;
      }

      const tooMany = tooManyPropertyPhotosError(
        formatPhotos.length,
        acceptedFiles.length
      );
      if (tooMany) {
        setImageError([tooMany]);
        return;
      }

      const imagesWithError: IImageError[] = [];
      let nextOrder = formatPhotos.length;
      let uploadedCount = 0;
      setLoadingPhotosRequest(true);
      setImageError([]);

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        await asyncForEach(acceptedFiles, async (file) => {
          try {
            const inspectionError = await inspectPropertyPhotoFile(file);
            if (inspectionError) {
              imagesWithError.push(inspectionError);
              return;
            }

            const imageBase64 = extractPropertyPhotoBase64(await loadFile(file));
            if (!imageBase64) {
              imagesWithError.push({
                name: file.name,
                reason: PHOTO_INVALID_TYPE_REASON,
              });
              return;
            }

            nextOrder += 1;
            const response = await api.post(
              `/api/adm/properties/${params.id}/photos`,
              {
                order: nextOrder,
                photo: imageBase64,
              }
            );

            if (!response.data.success) {
              imagesWithError.push({
                name: file.name,
                reason: getApiPhotoUploadReason(response.data),
              });
              return;
            }

            uploadedCount += 1;
            setPhotos((prevValue) => [
              ...prevValue,
              mapUploadedPropertyPhoto(response.data.data),
            ]);
          } catch (err: any) {
            if (err.response?.data?.errors) {
              Object.values(err.response.data.errors).forEach((messages) => {
                if (!Array.isArray(messages)) return;
                messages.forEach((error: string) => {
                  addToast({ title: error, type: "error" });
                });
              });
            }
            imagesWithError.push({
              name: file.name,
              reason: getApiPhotoUploadReason(err),
            });
          }
        });

        setImageError(imagesWithError);
        if (uploadedCount > 0) {
          addToast({
            title: "Imagens adicionadas!",
            type: "success",
          });
        }
      } finally {
        setLoadingPhotosRequest(false);
      }
    },
    [access_token, addToast, dateToExpires, formatPhotos.length, params.id]
  );

  const handleRemovePhoto = useCallback(
    async (id: number) => {
      setLoadingRequestToRemovePhoto(true);

      try {
        const token = await refreshToken(dateToExpires, access_token);

        api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
          }`;

        const response = await api.delete(`/api/adm/photos/${id}`);

        if (!!response.data.success) {
          const newArrayToShow = photos.filter((photo) => photo.id !== id);
          const filteredQueue = photosToReOrder.filter((entry) => entry.id !== id);
          const normalizedPhotosAfterRemoval = newArrayToShow.map((photo, index) => ({
            ...photo,
            order: index + 1,
          }));
          const normalizedFilteredQueue = Array.from(
            new Map(
              filteredQueue
                .filter(
                  (entry) =>
                    Number.isInteger(entry.newOrder) &&
                    entry.newOrder >= 1 &&
                    entry.newOrder <= normalizedPhotosAfterRemoval.length
                )
                .map((entry) => [entry.id, entry] as const)
            ).values()
          );

          addToast({
            title: "Imagem removida!",
            type: "success",
          });

          setPhotos(normalizedPhotosAfterRemoval);
          setPhotosToReOrder(normalizedFilteredQueue);
        }
      } catch (err: any) {
        if (!!err.response?.data.errors) {
          const errorsNumber = Object.keys(err.response.data.errors).length;

          for (let i = 0; i < errorsNumber; i++) {
            err.response.data.errors[
              Object.keys(err.response.data.errors)[i]
            ].forEach((error: string) => {
              addToast({
                title: error,
                type: "error",
              });
            });
          }
        }

        if (!!err.response?.data.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      }

      setLoadingRequestToRemovePhoto(false);
    },
    [access_token, dateToExpires, photos, photosToReOrder, addToast]
  );

  async function handleGenerateDescription() {
    try {
      setIsGeneratingDescription(true);

      const res = await api.post("/api/adm/properties/generate-description-chatgpt", {
        brief_description: briefDescription,
      });

      setDescription(res.data.description);
    } catch (err) {
      console.log(err);
      addToast({
        type: "error",
        title: "Erro ao gerar descrição com IA",
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  }


  const handleActivateModal = useCallback(async () => {
    setLoadingRequestToActivateProperty(true);

    try {
      const response = await api.post(
        `/api/adm/properties/${params.id}/activate`
      );

      if (!!response.data.success) {
        setFormErrors({});

        addToast({
          title: "Imóvel ativado!",
          type: "success",
        });

        handleRefresh();
      }
    } catch (err: any) {
      if (
        !!err.response?.data.errors &&
        !!err.response.data.errors[Object.keys(err.response.data.errors)[0]][0]
      ) {
        const errorsNumber = Object.keys(err.response.data.errors).length;

        for (let i = 0; i < errorsNumber; i++) {
          err.response.data.errors[
            Object.keys(err.response.data.errors)[i]
          ].forEach((error: string) => {
            addToast({
              title: error,
              type: "error",
            });
          });
        }
      }

      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setOpenModalConfirmActivateProperty(false);
    setLoadingRequestToActivateProperty(false);
  }, [params.id, addToast, handleRefresh]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...PHOTO_DROPZONE_OPTIONS,
    disabled: loadingPhotosRequest,
    onDrop: handleOnDrop,
  });

  const onGuaranteeSelectedHandler = (e: any) => {
    if (e.target.checked) {
      setSelectedGuarantees((prevGuarantees) => [
        ...prevGuarantees,
        Number(e.target.value),
      ]);
    } else {
      setSelectedGuarantees((prevGuarantees) =>
        prevGuarantees.filter((guarantee) => {
          return Number(e.target.value) !== guarantee;
        })
      );
    }
  };

  const propertyHasGuarantee = (guaranteeId: number) => {
    const hasGuarantee = selectedGuarantees.find(
      (guarantee) => guarantee === guaranteeId
    );
    return !!hasGuarantee;
  };

  async function handleShareLink() {
    setLoadingSharedLink(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;
      const response = await api.post(
        `/api/adm/properties/shared-link/${params.id}`
      );
      const URL =
        process.env.REACT_APP_ENVIROMENT === "deploy"
          ? "https://www.7cantos.com/imovel/"
          : "https://web-app7cantos-staging.herokuapp.com/imovel/";
      await navigator.clipboard.writeText(URL + `${response.data.data.key}`);
      addToast({
        title: "Link copiado para o Clipboard",
        type: "success",
      });
    } catch (err: any) {
      console.log(err);
      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }
    }
    setLoadingSharedLink(false);
  }


  const editPropertyWizardValue: EditPropertyWizardModel = {
    FLOOR_TYPE_OPTIONS,
    ZAP_PUBLICATION_OPTIONS,
    allowPets,
    area,
    bathrooms,
    briefDescription,
    condominium,
    condominiumValue,
    virtualTourUrl,
    consultantID,
    keyLocationId,
    keyLocationNote,
    keyLocationPassword,
    initialKeyLocationId,
    keyLocationCatalog,
    contactEmail,
    contactPhone,
    commission,
    setCommission,
    consultantsOptions,
    description,
    email,
    finderID,
    finderName,
    findersOptions,
    floorType,
    housingProgram,
    setHousingProgram,
    formErrors,
    setFormErrors,
    formOptionSelected,
    garage,
    garbageTax,
    gasIncluded,
    guarantees,
    handleActivateModal,
    handleGenerateDescription,
    handleGoToCharacteristicForm,
    handleGoToPhotosForm,
    handlePublishOnImovelWeb,
    handleSaveCharacteristicsDraft,
    handleSaveDraft,
    handleDraftNextFromMap,
    handleDraftNextFromDescription,
    handleDraftNextFromPublication,
    handleDraftNextFromCharacteristics,
    handleSearchChange,
    handleSelectUser,
    handleClearOwnerSearchResults,
    handleClearOwnerSelection,
    handleAddNewOwner,
    handleShareLink,
    has24hConcierge,
    hasAC,
    hasBBQ,
    hasBackyard,
    hasCableTV,
    hasElevator,
    hasGameRoom,
    hasGarden,
    hasGourmet,
    hasGourmetPorch,
    hasGym,
    hasHomeCinema,
    hasParty,
    hasPlayground,
    hasPool,
    hasPorch,
    hasSPA,
    hasSauna,
    hasSeaView,
    hasSports,
    hasStorage,
    hasTennisCourt,
    iptuValue,
    isAdm,
    isDraft,
    isFunished,
    isGeneratingDescription,
    isNearbyClinics,
    isNearbyMajorRoads,
    isNearbyMall,
    isNearbyPublicTransport,
    isNearbySchools,
    isRented,
    isSavingDraft,
    loadingFinders,
    loadingRequest,
    loadingRequestToActivateProperty,
    loadingSharedLink,
    loadingUsers,
    isOwnerDropdownOpen,
    loadingUsersRequest,
    note,
    number,
    numberOfGaragesSuitesOptions,
    numberOfRankOptions,
    numberOfRoomsBathroomsOptions,
    onGuaranteeSelectedHandler,
    openModalConfirmActivateProperty,
    openModalDeactivateProperty,
    openModalEditDeactivateProperty,
    ownerDetails,
    ownerEmail,
    ownerID,
    ownerUserData,
    params,
    propertyConsultant,
    propertyHasGuarantee,
    propertyType,
    rank,
    rentData,
    rentReasons,
    rentValue,
    rooms,
    setAllowPets,
    setArea,
    setBathrooms,
    setBriefDescription,
    setCondominium,
    setCondominiumValue,
    setVirtualTourUrl,
    setConsultantID,
    setKeyLocationId,
    setKeyLocationNote,
    setKeyLocationPassword,
    setContactEmail,
    setContactPhone,
    setDescription,
    setEmail,
    setFinderID,
    setFinderName,
    setFloorType,
    setFormOptionSelected,
    setGarage,
    setGarbageTax,
    setGasIncluded,
    setHas24hConcierge,
    setHasAC,
    setHasBBQ,
    setHasBackyard,
    setHasCableTV,
    setHasElevator,
    setHasGameRoom,
    setHasGarden,
    setHasGourmet,
    setHasGourmetPorch,
    setHasGym,
    setHasHomeCinema,
    setHasParty,
    setHasPlayground,
    setHasPool,
    setHasPorch,
    setHasSPA,
    setHasSauna,
    setHasSeaView,
    setHasSports,
    setHasStorage,
    setHasTennisCourt,
    setIptuValue,
    setIsAdm,
    setIsFunished,
    setIsNearbyClinics,
    setIsNearbyMajorRoads,
    setIsNearbyMall,
    setIsNearbyPublicTransport,
    setIsNearbySchools,
    setNote,
    setOpenModalConfirmActivateProperty,
    setOpenModalDeactivateProperty,
    setOpenModalEditDeactivateProperty,
    setOwnerDetails,
    setOwnerEmail,
    setOwnerID,
    setPropertyConsultant,
    setPropertyType,
    setRank,
    setRentValue,
    setRooms,
    setSuites,
    setTitle,
    setWaterIncluded,
    setYearBuilt,
    setZapPublicationType,
    suites,
    title,
    users,
    waterIncluded,
    yearBuilt,
    zapPublicationType,
  };

  return (
    <div>
      <Layout>
        {!!loadingApiData && !!loadingGoogleMapsApi ? (
          <Loading />
        ) : (
          <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center">
            <div className="mt-6 w-full px-3 py-6 md:px-6">
              <div className="flex items-center justify-start border-b border-slate-200/80 pb-6">
                <div className="flex w-full flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-3xl font-bold text-slate-800">
                        Editar imóvel
                      </h3>
                      {isDraft ? (
                        <DraftBadge status={PropertyStatus.PRE_REGISTERED} />
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {isDraft
                        ? "Este imóvel está em pré-cadastro (rascunho). Pode guardar o rascunho ou publicar."
                        : "Atualize os dados do imóvel nas etapas abaixo."}
                    </p>
                  </div>
                </div>
              </div>

              <WizardSteps
                items={EDIT_STEPS.map((step) => ({
                  id: step.id,
                  label: step.label,
                }))}
                activeId={formOptionSelected}
                isUnlocked={() => true}
                onSelect={(id) => {
                  setFormOptionSelected(id as EditPropertyStepId);
                  window.scrollTo(0, 0);
                }}
              />

              {formOptionSelected === "map" && (
                <EditMapStep
                  googleMapsSearch={googleMapsSearch}
                  setGoogleMapsSearch={setGoogleMapsSearch}
                  hadleGetMoreInformationsAboutLocal={
                    hadleGetMoreInformationsAboutLocal
                  }
                  googleID={googleID}
                  setGoogleID={setGoogleID}
                  formErrors={formErrors}
                  logradouro={logradouro}
                  setLogradouro={setLogradouro}
                  cep={cep}
                  setCEP={setCEP}
                  number={number}
                  setNumber={setNumber}
                  complement={complement}
                  setComplement={setComplement}
                  neighborhood={neighborhood}
                  setNeighborhood={setNeighborhood}
                  city={city}
                  setCity={setCity}
                  uf={uf}
                  setUF={setUF}
                  floor={floor}
                  setFloor={setFloor}
                  latitude={latitude}
                  longitude={longitude}
                  setLoadingGoogleMapsApi={setLoadingGoogleMapsApi}
                  loadingRequest={loadingRequest}
                  handleGoToDescriptionForm={handleGoToDescriptionForm}
                  setFormOptionSelected={setFormOptionSelected}
                  isDraft={isDraft}
                  isSavingDraft={isSavingDraft}
                  handleClearAddressFields={handleClearAddressFields}
                  handleSaveAddressDraft={handleSaveAddressDraft}
                  handleDraftNextFromMap={handleDraftNextFromMap}
                  propertyId={Number(params.id)}
                  rentReasons={rentReasons}
                  hasChildrenUnits={hasChildrenUnits}
                  onChildrenCountChange={(count) => {
                    setHasChildrenUnits(count > 0);
                    if (count > 0) {
                      setComplement("");
                      setFloor("");
                    }
                  }}
                />
              )}

              <EditPropertyWizardProvider value={editPropertyWizardValue}>
                <>
                  {formOptionSelected === "description" && (
                    <EditDescriptionStep />
                  )}
                  {formOptionSelected === "publication" && (
                    <EditPublicationStep />
                  )}
                  {formOptionSelected === "characteristic" && (
                    <EditCharacteristicsStep />
                  )}
                </>
              </EditPropertyWizardProvider>

              {formOptionSelected === "photos" && (
                <EditPhotosStep
                  propertyId={Number(params.id)}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  isDragActive={isDragActive}
                  loadingPhotosRequest={loadingPhotosRequest}
                  state={state}
                  imageError={imageError}
                  photos={photos}
                  loadingRequestToRemovePhoto={loadingRequestToRemovePhoto}
                  hadleDragImageBox={hadleDragImageBox}
                  handleDropImageBox={handleDropImageBox}
                  handleRemovePhoto={handleRemovePhoto}
                  onVirtualStaging={handleGenerateVirtualStaging}
                  onCustomEdit={openCustomEditPrompt}
                  generatingAI={generatingAI}
                  aiPreviews={aiPreviews}
                  isAIPreviewModalOpen={isPreviewModalOpen}
                  setIsAIPreviewModalOpen={setIsPreviewModalOpen}
                  handleApplyAI={handleApplyAI}
                  handleDiscardAI={handleDiscardAI}
                  handleTryAgainAI={handleTryAgainAI}
                  loadingRequest={loadingRequest}
                  handleChangeOrders={handleChangeOrders}
                  setFormOptionSelected={setFormOptionSelected}
                  isDraft={isDraft}
                  handlePublishDraft={handlePublishDraft}
                  isSavingDraft={isSavingDraft}
                  handleSavePhotosDraft={handleSavePhotosDraft}
                />
              )}

              {formOptionSelected === "history" && (
                <EditHistoryStep propertyId={Number(params.id)} />
              )}
            </div>
          </div>
        )}

        <PhotoAiPromptModal
          open={isPromptModalOpen}
          prompt={promptDraft}
          photoPreviewUrl={promptModalPhotoUrl}
          isLoading={isPromptModalLoading}
          onPromptChange={setPromptDraft}
          onClose={closeCustomEditPrompt}
          onSubmit={submitCustomEdit}
          onCancelProcessing={() => {
            if (promptModalPhotoId) cancelGeneration(promptModalPhotoId);
          }}
        />
      </Layout>
    </div>
  );
};

export default EditProperty;
